import { supabase } from '../config/supabase';

export interface PlanoTrabalhoDB {
  id: string;
  id_empresa: string;
  id_usuario: string;
  titulo: string;
  descricao?: string;
  observacoes_gerais?: string;
  data_inicio?: string;
  data_fim?: string;
  status: 'ativo' | 'inativo' | 'concluido' | 'pausado';
  created_at: string;
  updated_at: string;
}

export interface MetaDB {
  id: string;
  id_plano_trabalho: string;
  meta: string;
  ordem: number;
  created_at: string;
  updated_at: string;
}

export interface AcaoDB {
  id: string;
  id_plano_trabalho: string;
  descricao: string;
  responsavel?: string;
  prazo?: string;
  status: 'pendente' | 'em_andamento' | 'concluida' | 'cancelada' | 'pausada';
  prioridade: 'baixa' | 'media' | 'alta' | 'urgente';
  observacoes?: string;
  data_inicio?: string;
  data_conclusao?: string;
  data_prevista?: string;
  created_at: string;
  updated_at: string;
}

export interface ObjetivoDB {
  id: string;
  id_plano_trabalho: string;
  titulo: string;
  descricao?: string;
  ordem: number;
  created_at: string;
  updated_at: string;
}

export interface PlanoTrabalhoCompleto {
  plano: PlanoTrabalhoDB;
  metas: MetaDB[];
  objetivos: ObjetivoDB[];
  acoes: AcaoDB[];
}

class PlanoTrabalhoService {
  /**
   * Cria um novo plano de trabalho padrão para uma empresa
   */
  async criarPlanoPadrao(idEmpresa: string, idUsuario: string, titulo: string = 'Plano de Trabalho CIPA'): Promise<string> {
    try {
      console.log('Criando plano padrão para empresa:', idEmpresa, 'usuário:', idUsuario);
      
      // Criar o plano principal
      const { data: planoData, error: planoError } = await supabase
        .from('cp_plano_trabalho')
        .insert({
          id_empresa: idEmpresa,
          id_usuario: idUsuario,
          titulo: titulo,
          descricao: 'Plano de trabalho da CIPA conforme NR-05',
          observacoes_gerais: 'Este plano deve ser revisado e atualizado periodicamente conforme as necessidades da empresa.',
          data_inicio: new Date().toISOString().split('T')[0],
          status: 'ativo'
        })
        .select('id')
        .single();

      if (planoError) {
        console.error('Erro ao criar plano principal:', planoError);
        throw planoError;
      }

      const planoId = planoData.id;
      console.log('Plano criado com ID:', planoId);

      // Criar metas padrão
      const metasPadrao = [
        'Promover a saúde e segurança dos trabalhadores',
        'Identificar e controlar riscos ocupacionais',
        'Implementar medidas preventivas de acidentes'
      ];

      const metasData = metasPadrao.map((meta, index) => ({
        id_plano_trabalho: planoId,
        meta: meta,
        ordem: index + 1
      }));

      const { error: metasError } = await supabase
        .from('cp_plano_metas')
        .insert(metasData);

      if (metasError) {
        console.error('Erro ao criar metas padrão:', metasError);
        // Não falha se não conseguir criar as metas
      }

      console.log('Plano padrão criado com sucesso, ID:', planoId);
      return planoId;
    } catch (error) {
      console.error('Erro ao criar plano padrão:', error);
      throw error;
    }
  }

  /**
   * Obtém o plano de trabalho completo de uma empresa
   */
  async obterPlanoCompleto(idEmpresa: string): Promise<PlanoTrabalhoCompleto | null> {
    try {
      console.log('Buscando plano completo para empresa:', idEmpresa);
      
      // Buscar plano principal
      const { data: plano, error: planoError } = await supabase
        .from('cp_plano_trabalho')
        .select('*')
        .eq('id_empresa', idEmpresa)
        .eq('status', 'ativo')
        .single();

      if (planoError) {
        if (planoError.code === 'PGRST116') {
          console.log('Nenhum plano encontrado para a empresa:', idEmpresa);
          return null;
        }
        console.error('Erro ao buscar plano:', planoError);
        throw planoError;
      }

      if (!plano) {
        console.log('Nenhum plano encontrado para a empresa:', idEmpresa);
        return null;
      }

      // Buscar metas
      const { data: metas, error: metasError } = await supabase
        .from('cp_plano_metas')
        .select('*')
        .eq('id_plano_trabalho', plano.id)
        .order('ordem');

      if (metasError) {
        console.error('Erro ao buscar metas:', metasError);
      }

      // Buscar ações
      const { data: acoes, error: acoesError } = await supabase
        .from('cp_plano_acoes')
        .select('*')
        .eq('id_plano_trabalho', plano.id)
        .order('created_at');

      if (acoesError) {
        console.error('Erro ao buscar ações:', acoesError);
      }

      // Buscar objetivos
      const { data: objetivos, error: objetivosError } = await supabase
        .from('cp_plano_objetivos')
        .select('*')
        .eq('id_plano_trabalho', plano.id)
        .order('ordem');

      if (objetivosError) {
        console.error('Erro ao buscar objetivos:', objetivosError);
      }

      const planoCompleto: PlanoTrabalhoCompleto = {
        plano: plano,
        metas: metas || [],
        acoes: acoes || [],
        objetivos: objetivos || []
      };

      console.log('Plano completo carregado:', {
        plano: plano.id,
        metas: metas?.length || 0,
        acoes: acoes?.length || 0,
        objetivos: objetivos?.length || 0
      });

      return planoCompleto;
    } catch (error) {
      console.error('Erro ao obter plano completo:', error);
      return null;
    }
  }

  /**
   * Salva ou atualiza um plano de trabalho
   */
  async salvarPlano(plano: Partial<PlanoTrabalhoDB>): Promise<PlanoTrabalhoDB> {
    try {
      if (plano.id) {
        // Atualizar plano existente
        const { data, error } = await supabase
          .from('cp_plano_trabalho')
          .update({
            titulo: plano.titulo,
            descricao: plano.descricao,
            observacoes_gerais: plano.observacoes_gerais,
            data_inicio: plano.data_inicio,
            data_fim: plano.data_fim,
            status: plano.status
          })
          .eq('id', plano.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Criar novo plano
        const { data, error } = await supabase
          .from('cp_plano_trabalho')
          .insert({
            id_empresa: plano.id_empresa!,
            id_usuario: plano.id_usuario!,
            titulo: plano.titulo!,
            descricao: plano.descricao,
            observacoes_gerais: plano.observacoes_gerais,
            data_inicio: plano.data_inicio,
            data_fim: plano.data_fim,
            status: plano.status || 'ativo'
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Erro ao salvar plano:', error);
      throw error;
    }
  }

  /**
   * Salva ou atualiza uma meta
   */
  async salvarMeta(meta: Partial<MetaDB>): Promise<MetaDB> {
    try {
      if (meta.id) {
        // Atualizar meta existente
        const { data, error } = await supabase
          .from('cp_plano_metas')
          .update({
            meta: meta.meta,
            ordem: meta.ordem
          })
          .eq('id', meta.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Criar nova meta
        const { data, error } = await supabase
          .from('cp_plano_metas')
          .insert({
            id_plano_trabalho: meta.id_plano_trabalho!,
            meta: meta.meta!,
            ordem: meta.ordem || 0
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Erro ao salvar meta:', error);
      throw error;
    }
  }

  /**
   * Remove uma meta
   */
  async removerMeta(idMeta: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('cp_plano_metas')
        .delete()
        .eq('id', idMeta);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao remover meta:', error);
      throw error;
    }
  }

  /**
   * Salva ou atualiza uma ação
   */
  async salvarAcao(acao: Partial<AcaoDB>): Promise<AcaoDB> {
    try {
      if (acao.id) {
        // Atualizar ação existente
        const { data, error } = await supabase
          .from('cp_plano_acoes')
          .update({
            descricao: acao.descricao,
            responsavel: acao.responsavel,
            prazo: acao.prazo,
            status: acao.status,
            prioridade: acao.prioridade,
            observacoes: acao.observacoes,
            data_inicio: acao.data_inicio,
            data_conclusao: acao.data_conclusao,
            data_prevista: acao.data_prevista
          })
          .eq('id', acao.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Criar nova ação
        const { data, error } = await supabase
          .from('cp_plano_acoes')
          .insert({
            id_plano_trabalho: acao.id_plano_trabalho!,
            descricao: acao.descricao!,
            responsavel: acao.responsavel,
            prazo: acao.prazo,
            status: acao.status || 'pendente',
            prioridade: acao.prioridade || 'media',
            observacoes: acao.observacoes,
            data_inicio: acao.data_inicio,
            data_conclusao: acao.data_conclusao,
            data_prevista: acao.data_prevista
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Erro ao salvar ação:', error);
      throw error;
    }
  }

  /**
   * Remove uma ação
   */
  async removerAcao(idAcao: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('cp_plano_acoes')
        .delete()
        .eq('id', idAcao);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao remover ação:', error);
      throw error;
    }
  }

  /**
   * Salva ou atualiza um objetivo
   */
  async salvarObjetivo(objetivo: Partial<ObjetivoDB>): Promise<ObjetivoDB> {
    try {
      if (objetivo.id) {
        // Atualizar objetivo existente
        const { data, error } = await supabase
          .from('cp_plano_objetivos')
          .update({
            titulo: objetivo.titulo,
            descricao: objetivo.descricao,
            ordem: objetivo.ordem
          })
          .eq('id', objetivo.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Criar novo objetivo
        const { data, error } = await supabase
          .from('cp_plano_objetivos')
          .insert({
            id_plano_trabalho: objetivo.id_plano_trabalho!,
            titulo: objetivo.titulo!,
            descricao: objetivo.descricao,
            ordem: objetivo.ordem || 0
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Erro ao salvar objetivo:', error);
      throw error;
    }
  }

  /**
   * Remove um objetivo
   */
  async removerObjetivo(idObjetivo: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('cp_plano_objetivos')
        .delete()
        .eq('id', idObjetivo);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao remover objetivo:', error);
      throw error;
    }
  }

  /**
   * Registra uma alteração no histórico
   */
  async registrarHistorico(
    idPlano: string,
    idUsuario: string,
    tipoAlteracao: string,
    descricao: string,
    dadosAnteriores?: any,
    dadosNovos?: any
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('cp_plano_historico')
        .insert({
          id_plano_trabalho: idPlano,
          id_usuario: idUsuario,
          tipo_alteracao: tipoAlteracao,
          descricao: descricao,
          dados_anteriores: dadosAnteriores,
          dados_novos: dadosNovos
        });

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao registrar histórico:', error);
      throw error;
    }
  }
}

export const planoTrabalhoService = new PlanoTrabalhoService();
